import React from 'react';
import { useQuery } from '@apollo/client/react';
import i18n from 'i18next';
import Loading from '@components/core/loading';
import { FieldChangeGrid } from '@pages/admin/audit-log/change-grid';
import {
    getFieldChangeItemsAuditRecord,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from '@pages/admin/audit-log/service';
import { AuditServiceType } from 'types/graphql-global-types';
import { getPrefixByService } from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';

const propArray = ['OrgKey', 'InUse'];

const joinAttendeePropsToString = (recordData: Record<string, never> | null, recordIdPrefix?: string) =>
    ['FirstName', 'LastName', 'JobTitle', 'RecordId']
        .map((prop) => {
            const propName = i18n.t(`auditLog:${prop}`);
            const recordValue = recordData?.[prop] ?? '--';
            const propValue = prop === 'RecordId' ? `${recordIdPrefix}${recordValue}` : recordValue;
            return `${propName}: ${propValue}`;
        })
        .join('; ');

const RiskReviewRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    const { data: prefixesData, loading: isLoadingPrefixes } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        { fetchPolicy: 'cache-first' },
    );

    const formatAttendeeEntries = (recordData: Record<string, never>) => {
        if (!recordData) {
            return null;
        }

        const attendeesString = joinAttendeePropsToString(
            recordData,
            getPrefixByService(prefixesData?.auditRecordIdPrefixes || [], AuditServiceType.REPORTING),
        );

        if (recordData.SortOrder) {
            return `${recordData.SortOrder}: ${attendeesString}`;
        }

        return attendeesString;
    };

    const compareAttendeeEntries = (
        oldRecordData: { RecordId: string; SortOrder: number },
        newRecordData: { RecordId: string; SortOrder: number },
    ) => {
        return oldRecordData.RecordId !== newRecordData.RecordId || oldRecordData.SortOrder !== newRecordData.SortOrder;
    };

    const arrayOfObjectsV2: any = {
        Attendees: {
            valueResolver: formatAttendeeEntries,
            compareField: 'RecordId',
            valueComparer: compareAttendeeEntries,
        },
    };

    const refactoredOldRecord = {
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, propArray),
    };

    const refactoredNewRecord = {
        ...newRecord,
        ...removePropertiesFromObject(newRecord, propArray),
    };

    const customFieldRenderer = (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjectsV2,
        });

    const items = getFieldChangeItemsAuditRecord(refactoredOldRecord, refactoredNewRecord, null, customFieldRenderer);

    if (isLoadingPrefixes) return <Loading float />;

    return <FieldChangeGrid items={items} />;
};

export { RiskReviewRecordChangeGrid };
